import { useCallback, useState, useMemo, useRef, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Wrench, Zap, Wind, ArrowLeftRight, ClipboardList, BookOpen, Download, Star, Sun, Moon, Trash2, Search, X, ChevronDown, Clock, TrendingUp, Settings, WifiOff, CheckCircle2, HardHat, Heart, Mic } from "lucide-react";
import InlineConverter from "@/components/InlineConverter";
import { motion } from "framer-motion";
import { useRecentTools } from "@/hooks/use-recent-tools";
import { toast } from "sonner";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors, type DragEndEvent, type DragOverEvent, type DragStartEvent } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { restrictToVerticalAxis, restrictToParentElement } from "@dnd-kit/modifiers";
import { useIsStandalone } from "@/hooks/use-standalone";
import { useFavorites, FavoriteItem } from "@/hooks/use-favorites";
import { useTheme } from "@/hooks/use-theme";
import SortableFavoriteCard from "@/components/SortableFavoriteCard";
import SwipeToDelete from "@/components/SwipeToDelete";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useSpeechSearch } from "@/hooks/use-speech-search";
import { mainTools, subTools, searchChips } from "@/lib/search-index";

const favLinkMap: Record<FavoriteItem["type"], string> = {
  formula: "/electrical",
  pipe: "/pipe",
  wire: "/electrical",
  duct: "/ductwork",
  structural: "/structural",
};

const favGroupMeta: Record<FavoriteItem["type"], { label: string; icon: typeof Wrench }> = {
  pipe: { label: "Pipe Sizes", icon: Wrench },
  wire: { label: "Electrical", icon: Zap },
  formula: { label: "Formulas", icon: BookOpen },
  duct: { label: "Ductwork", icon: Wind },
  structural: { label: "Structural", icon: HardHat },
};

const allTools = mainTools;
const tools = allTools;

const Index = () => {
  const isStandalone = useIsStandalone();
  const { favorites, removeFavoriteWithUndo, clearAllFavorites, reorderFavorites } = useFavorites();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [toolSearch, setToolSearch] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const [recentSearches, setRecentSearches] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem("trades-recent-searches") || "[]"); } catch { return []; }
  });
  const saveRecentSearch = useCallback((query: string) => {
    const trimmed = query.trim();
    if (!trimmed || trimmed.length < 3) return;
    setRecentSearches(prev => {
      const updated = [trimmed, ...prev.filter(s => s.toLowerCase() !== trimmed.toLowerCase())].slice(0, 3);
      localStorage.setItem("trades-recent-searches", JSON.stringify(updated));
      return updated;
    });
  }, []);
  const removeRecentSearch = useCallback((query: string) => {
    setRecentSearches(prev => {
      const updated = prev.filter(s => s !== query);
      localStorage.setItem("trades-recent-searches", JSON.stringify(updated));
      return updated;
    });
  }, []);
  const randomChips = useMemo(() => {
    return [...searchChips].sort(() => Math.random() - 0.5).slice(0, 6);
  }, []);
  const [searchParams, setSearchParams] = useSearchParams();
  const { recentTools, trackVisit, clearRecent } = useRecentTools();
  const { isListening, isSupported: speechSupported, toggle: toggleSpeech } = useSpeechSearch({
    onResult: (transcript) => setToolSearch(transcript),
  });

  // Show toast when mic starts listening
  useEffect(() => {
    if (isListening) {
      toast.info("Listening… speak a tool name", { duration: 2000 });
    }
  }, [isListening]);
  // Cmd+K / Ctrl+K or '/' to focus search
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      }
      // '/' to focus search (when not in an input)
      if (
        e.key === "/" &&
        !e.metaKey && !e.ctrlKey && !e.altKey &&
        !(e.target instanceof HTMLInputElement) &&
        !(e.target instanceof HTMLTextAreaElement)
      ) {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Auto-focus when navigated with ?focus=search
  useEffect(() => {
    if (searchParams.get("focus") === "search") {
      setTimeout(() => { searchRef.current?.focus(); }, 100);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Smart search: scores results by relevance across labels, keywords, and problem descriptions
  // Supports multi-word queries — each word scores independently, results ranked by total score
  const searchResults = useMemo(() => {
    if (!toolSearch.trim()) return [];
    const q = toolSearch.toLowerCase().trim();
    const words = q.split(/\s+/).filter(w => w.length > 1);
    if (words.length === 0) return [];

    const stopWords = new Set(["i", "a", "the", "to", "for", "of", "in", "do", "is", "my", "me", "it", "an", "on", "at", "we", "can"]);
    const meaningfulWords = words.filter(w => !stopWords.has(w));
    const searchWords = meaningfulWords.length > 0 ? meaningfulWords : words;

    const scoreText = (text: string) => {
      let score = 0;
      const lower = text.toLowerCase();
      // Exact full query match is highest
      if (lower.includes(q)) score += 10;
      // Each word match adds score
      for (const w of searchWords) {
        if (lower.includes(w)) score += 3;
      }
      return score;
    };

    type ScoredResult = { path: string; label: string; desc: string; icon: typeof Wrench; color: string; toolId: string | undefined; score: number };

    const results: ScoredResult[] = [];

    // Score main tools
    for (const t of allTools) {
      const score = scoreText(t.label) + scoreText(t.desc) + scoreText(t.keywords);
      if (score > 0) {
        results.push({ path: t.path, label: t.label, desc: t.desc, icon: t.icon, color: t.color, toolId: undefined, score });
      }
    }

    // Score sub-tools (including problems field)
    for (const t of subTools) {
      const parentMeta = allTools.find(m => m.path === t.path);
      const score = scoreText(t.label) + scoreText(t.desc) + scoreText(t.keywords) + scoreText(t.problems || "");
      if (score > 0) {
        results.push({
          path: t.path,
          label: t.label,
          desc: `${t.parent} → ${t.desc.replace(/^Formulas → /, "")}`,
          icon: parentMeta?.icon ?? BookOpen,
          color: parentMeta?.color ?? "hsl(var(--steel-light))",
          toolId: t.toolId,
          score,
        });
      }
    }

    // Deduplicate, sort by score descending
    const seen = new Set<string>();
    return results
      .sort((a, b) => b.score - a.score)
      .filter(r => { if (seen.has(r.label)) return false; seen.add(r.label); return true; })
      .slice(0, 8);
  }, [toolSearch]);

  // Time-of-day greeting
  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 5) return "🌙 Working late";
    if (h < 12) return "☀️ Good morning";
    if (h < 17) return "🔧 Good afternoon";
    return "🌆 Good evening";
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor),
  );


  const [favSearch, setFavSearch] = useState("");
  const [favTypeFilter, setFavTypeFilter] = useState<FavoriteItem["type"] | null>(null);
  const [favsCollapsed, setFavsCollapsed] = useState(() => {
    try { return localStorage.getItem("trades-toolbelt-favs-collapsed") === "true"; } catch { return false; }
  });
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    setOverId(event.over ? String(event.over.id) : null);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);
      setOverId(null);
      if (!over || active.id === over.id) return;

      const oldIndex = favorites.findIndex((f) => f.id === active.id);
      const newIndex = favorites.findIndex((f) => f.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      reorderFavorites(arrayMove(favorites, oldIndex, newIndex));
    },
    [favorites, reorderFavorites],
  );

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
    setOverId(null);
  }, []);

  return (
    <div className="pb-24 md:pb-8 animate-fade-in max-w-5xl mx-auto">
      <div className="px-4 pt-6 pb-2 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-foreground">
            {greeting}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Trades Toolbelt — {favorites.length} favorite{favorites.length !== 1 ? "s" : ""} saved
          </p>
          <div className="flex items-center gap-1.5 mt-1.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-success" />
            <span className="text-[11px] font-medium text-success-foreground">Offline-ready — all tools work without internet</span>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <Link
            to="/settings"
            className="p-2 rounded-lg bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors"
            aria-label="Settings"
          >
            <Settings className="w-5 h-5" />
          </Link>
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Global Tool Search */}
      <div className="px-4 mt-3 relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
          <input
            ref={searchRef}
            type="search"
            value={toolSearch}
            onChange={(e) => setToolSearch(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
            placeholder="Describe your problem or search tools… ⌘K"
            aria-label="Search tools by problem or name"
            role="combobox"
            aria-expanded={searchResults.length > 0}
            aria-autocomplete="list"
            className={`w-full bg-secondary border border-border rounded-xl pl-9 ${toolSearch ? "pr-9" : speechSupported ? "pr-9" : "pr-3"} py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all`}
          />
          {toolSearch ? (
            <button onClick={() => setToolSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Clear search">
              <X className="w-4 h-4" />
            </button>
          ) : speechSupported ? (
            <button
              onClick={toggleSpeech}
              className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${isListening ? "text-destructive animate-pulse" : "text-muted-foreground hover:text-foreground"}`}
              aria-label={isListening ? "Stop voice search" : "Search by voice"}
            >
              <Mic className="w-4 h-4" />
            </button>
          ) : null}
        </div>
        {/* Recent searches & suggestion chips when focused and no query */}
        {searchFocused && !toolSearch.trim() && searchResults.length === 0 && (
          <div className="mt-2 animate-fade-in space-y-2">
            {recentSearches.length > 0 && (
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <Clock className="w-3 h-3 text-muted-foreground" />
                  <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Recent</span>
                  <button
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { setRecentSearches([]); localStorage.removeItem("trades-recent-searches"); }}
                    className="ml-auto text-[10px] text-muted-foreground hover:text-destructive transition-colors"
                  >
                    Clear all
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {recentSearches.map((chip) => (
                    <span
                      key={chip}
                      className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 text-[11px] font-medium rounded-full bg-muted text-muted-foreground border border-border hover:bg-accent hover:text-accent-foreground transition-colors"
                    >
                      <button
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => { setToolSearch(chip); searchRef.current?.focus(); }}
                      >
                        {chip}
                      </button>
                      <button
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => removeRecentSearch(chip)}
                        className="ml-0.5 p-0.5 rounded-full hover:bg-destructive/20 hover:text-destructive transition-colors"
                        aria-label={`Remove ${chip}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div>
              <div className="flex items-center gap-1 mb-1">
                <Search className="w-3 h-3 text-muted-foreground" />
                <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Try</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {randomChips.map((chip) => (
                  <button
                    key={chip}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { saveRecentSearch(chip); setToolSearch(chip); searchRef.current?.focus(); }}
                    className="px-2.5 py-1 text-[11px] font-medium rounded-full bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors"
                  >
                    {chip}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
        {searchResults.length > 0 && (
          <div role="listbox" aria-label="Search results" className="absolute left-4 right-4 top-full mt-1 bg-card border border-border rounded-xl shadow-lg overflow-hidden z-50">
            {searchResults.map((result, i) => (
              <button
                key={`${result.label}-${i}`}
                onClick={() => { saveRecentSearch(toolSearch); navigate(result.toolId ? `${result.path}?tool=${result.toolId}` : result.path); setToolSearch(""); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-secondary transition-colors border-b border-border last:border-b-0"
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${result.color}15` }}>
                  <result.icon className="w-4 h-4" style={{ color: result.color }} />
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground">{result.label}</div>
                  <div className="text-[11px] text-muted-foreground">{result.desc}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Recently Used Tools */}
      {recentTools.length > 0 && (
        <div className="px-4 mt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Recently Used</h2>
            </div>
            <button onClick={clearRecent} className="text-[11px] text-muted-foreground hover:text-destructive transition-colors">
              Clear
            </button>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {recentTools.map((recent) => {
              const toolMeta = allTools.find(t => t.path === recent.path);
              const Icon = toolMeta?.icon ?? Zap;
              const color = toolMeta?.color ?? "hsl(var(--primary))";
              return (
                <Link
                  key={recent.path}
                  to={recent.path}
                  onClick={() => trackVisit(recent.path, recent.label)}
                  className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2 hover:border-primary/40 transition-colors active:scale-[0.97] shrink-0"
                >
                  <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
                    <Icon className="w-3.5 h-3.5" style={{ color }} />
                  </div>
                  <div className="text-left">
                    <div className="text-xs font-semibold text-foreground whitespace-nowrap">{recent.label}</div>
                    <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                      <TrendingUp className="w-2.5 h-2.5" />
                      {recent.visitCount} visit{recent.visitCount !== 1 ? "s" : ""}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Favorites Section – grouped by type */}
      <div className="px-4 mt-4">
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={() => setFavsCollapsed(prev => { const next = !prev; try { localStorage.setItem("trades-toolbelt-favs-collapsed", String(next)); } catch {} return next; })}
            className="flex items-center gap-2 group"
          >
            <Star className="w-4 h-4 fill-primary text-primary" />
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Favorites
              {favorites.length > 0 && (
                <span className="ml-2 text-[10px] font-bold bg-primary/15 text-primary rounded-full px-1.5 py-0.5 leading-none">
                  {favorites.length}
                </span>
              )}
            </h2>
            <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform duration-200 ${favsCollapsed ? "-rotate-90" : ""}`} />
          </button>
          {favorites.length > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-destructive transition-colors">
                  <Trash2 className="w-3 h-3" />
                  Clear all
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Clear all favorites?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will remove {favorites.length} favorite{favorites.length !== 1 ? "s" : ""}. You can undo this action from the toast notification.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={clearAllFavorites} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                    Clear all
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
        {!favsCollapsed && favorites.length > 0 && (
          <div className="space-y-2 mb-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input
                type="text"
                value={favSearch}
                onChange={(e) => setFavSearch(e.target.value)}
                placeholder="Filter favorites…"
                className="w-full bg-secondary/50 border border-border rounded-lg pl-8 pr-8 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
              />
              {favSearch && (
                <button onClick={() => setFavSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="flex gap-1.5">
              {(["pipe", "wire", "formula"] as const).map((type) => {
                const meta = favGroupMeta[type];
                const count = favorites.filter((f) => f.type === type).length;
                if (count === 0) return null;
                const active = favTypeFilter === type;
                return (
                  <button
                    key={type}
                    onClick={() => setFavTypeFilter(active ? null : type)}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors border ${
                      active
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-secondary/50 text-muted-foreground border-border hover:border-primary/40"
                    }`}
                  >
                    <meta.icon className="w-3 h-3" />
                    {meta.label}
                    <span className={`text-[10px] ${active ? "text-primary-foreground/70" : "text-muted-foreground/60"}`}>{count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {!favsCollapsed && (() => {
          const filtered = favorites.filter((f) => {
            if (favTypeFilter && f.type !== favTypeFilter) return false;
            if (favSearch) {
              const q = favSearch.toLowerCase();
              return f.name.toLowerCase().includes(q) || f.detail.toLowerCase().includes(q) || f.category.toLowerCase().includes(q);
            }
            return true;
          });
          return filtered.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
             <SortableContext items={filtered.map((f) => f.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2" key={favTypeFilter ?? "all"}>
                {filtered.map((fav, i) => {
                  const showHeader = i === 0 || fav.type !== filtered[i - 1].type;
                  const meta = favGroupMeta[fav.type];
                  const Icon = meta.icon;
                  const count = filtered.filter((f) => f.type === fav.type).length;

                  const activeIndex = activeId ? filtered.findIndex((f) => f.id === activeId) : -1;
                  const overIndex = overId ? filtered.findIndex((f) => f.id === overId) : -1;
                  const showDropBefore = activeId && overId && activeId !== overId && fav.id === overId && activeIndex > overIndex;
                  const showDropAfter = activeId && overId && activeId !== overId && fav.id === overId && activeIndex < overIndex;

                  return (
                    <div key={fav.id} className="animate-fade-in" style={{ animationDelay: `${i * 30}ms`, animationFillMode: "both" }}>
                      {showHeader && (
                        <div className={`flex items-center gap-1.5 mb-1.5 ${i > 0 ? "mt-3" : ""}`}>
                          <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{meta.label}</span>
                          <span className="ml-1 text-[10px] font-bold bg-primary/15 text-primary rounded-full px-1.5 py-0.5 leading-none">{count}</span>
                        </div>
                      )}
                      {showDropBefore && (
                        <div className="h-0.5 bg-primary rounded-full mx-2 my-1 animate-pulse" />
                      )}
                      <SwipeToDelete onDelete={() => removeFavoriteWithUndo(fav)}>
                        <SortableFavoriteCard fav={fav} linkTo={favLinkMap[fav.type] || "/formulas"} />
                      </SwipeToDelete>
                      {showDropAfter && (
                        <div className="h-0.5 bg-primary rounded-full mx-2 my-1 animate-pulse" />
                      )}
                    </div>
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        ) : favorites.length === 0 ? (
          <div className="bg-card border border-dashed border-border rounded-xl p-6 text-center">
            <Star className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
            <p className="text-sm font-medium text-muted-foreground">No favorites yet</p>
            <p className="text-xs text-muted-foreground mt-1">Tap the star icon on any pipe size, wire, or formula to save it here</p>
          </div>
        ) : (
          <div className="bg-card border border-dashed border-border rounded-xl p-4 text-center">
            <p className="text-sm text-muted-foreground">No favorites match "{favSearch}"</p>
          </div>
        );
        })()}
      </div>

      {/* Tools Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 px-4 mt-4">
        {tools.map((tool, i) => (
          <motion.div
            key={tool.path}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05, duration: 0.3, ease: "easeOut" as const }}
          >
            <Link
              to={tool.path}
              onClick={() => trackVisit(tool.path, tool.label)}
              className="bg-card border border-border rounded-xl p-4 flex flex-col gap-2 hover:border-primary/40 hover:shadow-md transition-all active:scale-[0.97] h-full"
            >
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${tool.color}15` }}
              >
                <tool.icon className="w-5 h-5" style={{ color: tool.color }} />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">{tool.label}</p>
                <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">{tool.desc}</p>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>

      {/* Quick Unit Converter */}
      <div className="px-4 mt-4">
        <div className="flex items-center gap-2 mb-2">
          <ArrowLeftRight className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Unit Converter</h2>
        </div>
        <InlineConverter />
      </div>

      {!isStandalone && (
        <div className="px-4 mt-6">
          <Link
            to="/install"
            className="flex items-center gap-3 bg-card border border-border rounded-xl p-4 hover:border-primary/40 transition-colors"
          >
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Download className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">Install App</p>
              <p className="text-[11px] text-muted-foreground">Add to your home screen for quick access</p>
            </div>
          </Link>
        </div>
      )}

      {/* Value 4 Value subtle banner */}
      <div className="px-4 mt-6">
        <Link
          to="/support"
          className="flex items-center gap-3 bg-card border border-border rounded-xl p-4 hover:border-primary/40 transition-colors"
        >
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Heart className="w-5 h-5 text-primary" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">Value 4 Value</p>
            <p className="text-[11px] text-muted-foreground">Free forever — if it helps, consider giving back</p>
          </div>
        </Link>
      </div>
    </div>
  );
};

export default Index;
